import { useEffect, useState } from "react";
import { api, type Account, type Role } from "./api";
import { Link } from "./Link";
import { roleLabels } from "./Profile";
import { Field, Notice } from "./ui";

interface Person {
  id: string;
  email: string;
  name: string | null;
  roles: Role[];
  playerId: string | null;
  playerName: string | null;
  lastSignInAt: number | null;
}
interface ClaimRequest {
  id: string;
  email: string;
  name: string | null;
  accountCreatedAt: number;
  playerName: string;
  tournamentCount: number;
}

const DAY_MS = 86400000;
/** Claims from brand-new accounts deserve a closer look, so the age is spelled out. */
function accountAge(createdAt: number): string {
  const days = Math.floor((Date.now() - createdAt) / DAY_MS);
  if (days < 1) return "account created today";
  return `account created ${days} ${days === 1 ? "day" : "days"} ago`;
}
const allRoles: Role[] = ["admin", "coach", "player"];

export function Admin({ account, go }: { account: Account; go: (href: string) => void }) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [claims, setClaims] = useState<ClaimRequest[] | null>(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState("");
  useEffect(() => {
    let current = true;
    setError("");
    Promise.all([
      api<{ people: Person[] }>("/admin/people"),
      api<{ claims: ClaimRequest[] }>("/admin/claims"),
    ])
      .then(([p, c]) => {
        if (!current) return;
        setPeople(p.people);
        setClaims(c.claims);
      })
      .catch((e: Error) => current && setError(e.message));
    return () => {
      current = false;
    };
  }, [reload]);
  const refresh = () => setReload((n) => n + 1);
  const q = query.trim().toLocaleLowerCase();
  const shown = people?.filter((person) =>
    `${person.email} ${person.name ?? ""} ${person.playerName ?? ""}`.toLocaleLowerCase().includes(q),
  );
  return (
    <main id="main" className="app-main">
      <div className="tournament-heading">
        <h1>People and access</h1>
        <p>
          Invite coaches and admins, set roles, and confirm which tournament
          record belongs to whom. To link a player by email, open Players &
          points on the{" "}
          <Link go={go} href="/">
            Tournaments
          </Link>{" "}
          page.
        </p>
      </div>
      {error && (
        <Notice error>
          {error}{" "}
          <button className="text-button" onClick={refresh}>
            Retry
          </button>
        </Notice>
      )}
      <div className="admin-layout">
        <section className="stack">
          <h2>Claims to review</h2>
          {!claims ? (
            !error && <Notice>Loading claims…</Notice>
          ) : claims.length === 0 ? (
            <p>No claims waiting.</p>
          ) : (
            <ul className="plain-list claim-list">
              {claims.map((claim) => (
                <ClaimRow key={claim.id} claim={claim} decided={refresh} />
              ))}
            </ul>
          )}
          <Invite invited={refresh} />
        </section>
        <section className="stack">
          <div className="section-heading">
            <h2>Accounts</h2>
            {people && <small>{people.length} total</small>}
          </div>
          <Field label="Find a person">
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
          </Field>
          {!shown ? (
            !error && <Notice>Loading accounts…</Notice>
          ) : shown.length === 0 ? (
            <p>No account matches that search.</p>
          ) : (
            <ul className="plain-list people-list">
              {shown.map((person) => (
                <PersonRow key={person.id} person={person} self={person.id === account.id} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

function ClaimRow({ claim, decided }: { claim: ClaimRequest; decided: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const decide = async (decision: "approve" | "decline") => {
    setBusy(true);
    setError("");
    try {
      await api(`/admin/claims/${claim.id}`, { decision });
      decided();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <li>
      <p>
        <strong>{claim.email}</strong>
        {claim.name && <> ({claim.name})</>} says they are{" "}
        <strong>{claim.playerName}</strong>{" "}
        <small>
          ({claim.tournamentCount} {claim.tournamentCount === 1 ? "tournament" : "tournaments"})
        </small>
      </p>
      <small>
        {accountAge(claim.accountCreatedAt)}. Check the email address belongs
        to this player before you link it.
      </small>
      {error && <Notice error>{error}</Notice>}
      <div className="actions">
        <button className="secondary" disabled={busy} onClick={() => void decide("approve")}>
          Link account
        </button>
        <button className="text-button" disabled={busy} onClick={() => void decide("decline")}>
          Decline
        </button>
      </div>
    </li>
  );
}

function Invite({ invited }: { invited: () => void }) {
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<Role[]>(["coach"]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  return (
    <form
      className="stack invite-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setMessage("");
        try {
          await api("/admin/people", { email, roles });
          setMessage(`Invite sent to ${email.trim().toLowerCase()}.`);
          setEmail("");
          invited();
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Invite a coach or admin</h2>
      <Field label="Email address">
        <input
          type="email"
          autoComplete="off"
          required
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <fieldset className="role-choices">
        <legend>Access</legend>
        {(["coach", "admin"] as Role[]).map((role) => (
          <label key={role} className="check">
            <input
              type="checkbox"
              checked={roles.includes(role)}
              onChange={(e) =>
                setRoles((current) =>
                  e.target.checked ? [...current, role] : current.filter((r) => r !== role),
                )
              }
            />
            {roleLabels[role]}
          </label>
        ))}
      </fieldset>
      {message && <Notice>{message}</Notice>}
      {error && <Notice error>{error}</Notice>}
      <div className="actions">
        <button className="primary" disabled={busy || !email.trim() || roles.length === 0}>
          {busy ? "Sending…" : "Send invite"}
        </button>
      </div>
    </form>
  );
}

function PersonRow({ person, self }: { person: Person; self: boolean }) {
  const [roles, setRoles] = useState(person.roles);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signedOut, setSignedOut] = useState(false);
  const signOut = async () => {
    setBusy(true);
    setError("");
    try {
      await api(`/admin/people/${person.id}/sign-out`, {});
      setSignedOut(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (role: Role, on: boolean) => {
    const wanted = on ? [...roles, role] : roles.filter((r) => r !== role);
    setBusy(true);
    setError("");
    try {
      const result = await api<{ roles: Role[] }>(`/admin/people/${person.id}/roles`, { roles: wanted }, "PUT");
      setRoles(result.roles);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <li>
      <div>
        <strong>{person.name ?? person.email}</strong>
        <small>
          {[
            person.name ? person.email : null,
            person.playerName ? `Plays as ${person.playerName}` : null,
            person.lastSignInAt ? null : "Hasn't signed in yet",
          ]
            .filter(Boolean)
            .join(" · ")}
        </small>
      </div>
      <fieldset className="role-choices" disabled={busy}>
        <legend className="sr-only">Roles for {person.email}</legend>
        {allRoles.map((role) => (
          <label key={role} className="check">
            <input
              type="checkbox"
              checked={roles.includes(role)}
              disabled={self && role === "admin"}
              onChange={(e) => void toggle(role, e.target.checked)}
            />
            {roleLabels[role]}
          </label>
        ))}
      </fieldset>
      {!self && (
        <div className="actions">
          <button type="button" className="text-button" disabled={busy || signedOut} onClick={() => void signOut()}>
            {signedOut ? "Signed out everywhere" : "Sign out everywhere"}
          </button>
        </div>
      )}
      {error && <Notice error>{error}</Notice>}
    </li>
  );
}
